import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { ShoppingCart, Eye } from "lucide-react";
import { supabase } from "@/supabase";
import { useToast } from "@/hooks/use-toast";
import AdminLayout from "@/components/admin/AdminLayout";
import AuthGuard from "@/components/admin/AuthGuard";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

interface OrderItem {
  id: string;
  product_id: string;
  product_name: string;
  product_color: string | null;
  product_size: string | null;
  quantity: number;
  unit_price: number;
  total_price: number;
}

interface Order {
  id: string;
  customer_name: string;
  customer_email: string;
  customer_phone: string;
  customer_address: string;
  customer_city: string | null;
  customer_state: string | null;
  customer_zipcode: string | null;
  payment_method: string | null;
  payment_status: string | null;
  order_status: string;
  notes: string | null;
  created_at: string;
  order_items: OrderItem[];
}

const statusOptions = [
  { value: "pending", label: "Pendente" },
  { value: "paid", label: "Pago" },
  { value: "preparing", label: "Preparando" },
  { value: "shipped", label: "Enviado" },
  { value: "delivered", label: "Entregue" },
  { value: "cancelled", label: "Cancelado" },
];

const statusLabels: Record<string, { label: string; class: string }> = {
  pending: { label: "Pendente", class: "bg-amber-100 text-amber-700" },
  paid: { label: "Pago", class: "bg-green-100 text-green-700" },
  preparing: { label: "Preparando", class: "bg-blue-100 text-blue-700" },
  shipped: { label: "Enviado", class: "bg-purple-100 text-purple-700" },
  delivered: { label: "Entregue", class: "bg-green-100 text-green-700" },
  cancelled: { label: "Cancelado", class: "bg-red-100 text-red-700" },
};

const AdminOrders = () => {
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const { data: orders, isLoading } = useQuery({
    queryKey: ["admin-orders", statusFilter],
    queryFn: async () => {
      let query = supabase
        .from("orders")
        .select(`
          *,
          order_items (
            id,
            product_id,
            product_name,
            product_color,
            product_size,
            quantity,
            unit_price,
            total_price
          )
        `)
        .order("created_at", { ascending: false });

      if (statusFilter !== "all") {
        query = query.eq("order_status", statusFilter as "pending" | "paid" | "preparing" | "shipped" | "delivered" | "cancelled");
      }

      const { data, error } = await query;
      if (error) throw error;
      return data.map((order) => ({
        ...order,
        order_items: Array.isArray(order.order_items)
          ? order.order_items
          : [],
      })) as Order[];
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: "pending" | "paid" | "preparing" | "shipped" | "delivered" | "cancelled" }) => {
      const { error } = await supabase
        .from("orders")
        .update({ order_status: status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["admin-orders"] });
      toast({ title: "Status atualizado com sucesso!" });
    },
    onError: (error: Error) => {
      toast({ title: "Erro ao atualizar status", description: error.message, variant: "destructive" });
    },
  });

  const formatDate = (date: string) => {
    return new Date(date).toLocaleDateString("pt-BR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const calculateOrderTotal = (order: Order) => {
    return order.order_items?.reduce((sum, item) => sum + Number(item.total_price), 0) || 0;
  };

  const getOrderItemsCount = (order: Order) => {
    return order.order_items?.reduce((sum, item) => sum + item.quantity, 0) || 0;
  };

  return (
    <AuthGuard>
      <AdminLayout>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-display text-3xl font-semibold text-foreground">
              Pedidos
            </h1>
            <p className="text-muted-foreground mt-1">
              Gerencie os pedidos da sua loja
            </p>
          </div>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="Filtrar por status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos os pedidos</SelectItem>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {isLoading ? (
          <div className="text-center py-12 text-muted-foreground">Carregando...</div>
        ) : orders && orders.length > 0 ? (
          <div className="admin-card overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Data</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Cliente</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Itens</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Valor</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-muted-foreground">Ações</th>
                </tr>
              </thead>
              <tbody>
                {orders.map((order) => (
                  <tr key={order.id} className="border-b border-border last:border-0 hover:bg-muted/50">
                    <td className="py-3 px-4 text-sm">{formatDate(order.created_at)}</td>
                    <td className="py-3 px-4 text-sm">{order.customer_name}</td>
                    <td className="py-3 px-4 text-sm">
                      {getOrderItemsCount(order)} {getOrderItemsCount(order) === 1 ? 'item' : 'itens'}
                    </td>
                    <td className="py-3 px-4 text-sm font-medium">
                      R$ {calculateOrderTotal(order).toFixed(2).replace(".", ",")}
                    </td>
                    <td className="py-3 px-4">
                      <Select
                        value={order.order_status}
                        onValueChange={(value) =>
                          updateStatusMutation.mutate({ id: order.id, status: value as "pending" | "paid" | "preparing" | "shipped" | "delivered" | "cancelled" })
                        }
                      >
                        <SelectTrigger className="w-32 h-8">
                          <span
                            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
                              statusLabels[order.order_status]?.class || ""
                            }`}
                          >
                            {statusLabels[order.order_status]?.label || order.order_status}
                          </span>
                        </SelectTrigger>
                        <SelectContent>
                          {statusOptions.map((option) => (
                            <SelectItem key={option.value} value={option.value}>
                              {option.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </td>
                    <td className="py-3 px-4">
                      <button
                        onClick={() => setSelectedOrder(order)}
                        className="p-2 hover:bg-muted rounded-lg transition-colors"
                        title="Ver detalhes"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="admin-card text-center py-12">
            <ShoppingCart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">Nenhum pedido encontrado.</p>
          </div>
        )}

        {/* Order Details Dialog */}
        <Dialog open={!!selectedOrder} onOpenChange={() => setSelectedOrder(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="font-display text-xl">Detalhes do Pedido</DialogTitle>
            </DialogHeader>
            {selectedOrder && (
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Data</p>
                    <p className="font-medium">{formatDate(selectedOrder.created_at)}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Status</p>
                    <span
                      className={`px-2 py-1 rounded-full text-xs font-medium ${
                        statusLabels[selectedOrder.order_status]?.class || ""
                      }`}
                    >
                      {statusLabels[selectedOrder.order_status]?.label}
                    </span>
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <h3 className="font-semibold mb-2">Cliente</h3>
                  <p>{selectedOrder.customer_name}</p>
                  <p className="text-sm text-muted-foreground">{selectedOrder.customer_email}</p>
                  <p className="text-sm text-muted-foreground">{selectedOrder.customer_phone}</p>
                </div>

                <div className="border-t border-border pt-4">
                  <h3 className="font-semibold mb-2">Endereço</h3>
                  <p className="text-sm">{selectedOrder.customer_address}</p>
                  {selectedOrder.customer_city && (
                    <p className="text-sm">
                      {selectedOrder.customer_city}
                      {selectedOrder.customer_state && ` - ${selectedOrder.customer_state}`}
                    </p>
                  )}
                  {selectedOrder.customer_zipcode && (
                    <p className="text-sm">CEP: {selectedOrder.customer_zipcode}</p>
                  )}
                </div>

                <div className="border-t border-border pt-4">
                  <h3 className="font-semibold mb-3">Itens do Pedido</h3>
                  <div className="space-y-3">
                    {selectedOrder.order_items?.map((item) => (
                      <div key={item.id} className="bg-muted/30 rounded-lg p-3">
                        <div className="flex justify-between items-start mb-2">
                          <div className="flex-1">
                            <p className="font-medium">{item.product_name}</p>
                            <div className="flex gap-3 text-sm text-muted-foreground mt-1">
                              {item.product_color && (
                                <span>Cor: {item.product_color}</span>
                              )}
                              {item.product_size && (
                                <span>Tamanho: {item.product_size}</span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="font-medium">
                              R$ {Number(item.total_price).toFixed(2).replace(".", ",")}
                            </p>
                          </div>
                        </div>
                        <div className="flex justify-between text-sm text-muted-foreground">
                          <span>Quantidade: {item.quantity}</span>
                          <span>
                            Unitário: R$ {Number(item.unit_price).toFixed(2).replace(".", ",")}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="border-t border-border pt-4">
                  <div className="flex justify-between items-center">
                    <span className="font-semibold">Total do Pedido</span>
                    <span className="text-xl font-bold text-primary">
                      R$ {calculateOrderTotal(selectedOrder).toFixed(2).replace(".", ",")}
                    </span>
                  </div>
                </div>

                {selectedOrder.notes && (
                  <div className="border-t border-border pt-4">
                    <h3 className="font-semibold mb-2">Observações</h3>
                    <p className="text-sm">{selectedOrder.notes}</p>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </AdminLayout>
    </AuthGuard>
  );
};

export default AdminOrders;
